import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/App';
import { Printer, Wifi, WifiOff, Volume2, VolumeX, Settings, ArrowLeft, CheckCircle, Clock, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import printJS from 'print-js';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const PrintStation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [connected, setConnected] = useState(false);
  const [autoPrint, setAutoPrint] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [printedOrders, setPrintedOrders] = useState([]);
  const [printingOrderId, setPrintingOrderId] = useState(null);
  const wsRef = useRef(null);
  const audioRef = useRef(null);

  // Fetch store data
  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await axios.get(`${API}/stores/me`);
        setStore(res.data);
      } catch (e) {
        toast.error('Erro ao carregar dados da loja');
      }
    };
    fetchStore();
  }, []);

  // Generate print HTML for thermal printer
  const generatePrintHTML = useCallback((order) => {
    const isDelivery = order.delivery_type === 'delivery';
    const storeName = store?.name || 'ADEGGA DELIVERY';
    
    const items = (order.items || []).map(item => `
      <tr>
        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc;">
          ${item.quantity}x ${item.product_name}
          ${item.variant_name ? `<br><small style="color: #666;">- ${item.variant_name}</small>` : ''}
          ${item.notes ? `<br><small style="color: #f59e0b; font-style: italic;">Obs: ${item.notes}</small>` : ''}
        </td>
        <td style="padding: 4px 0; border-bottom: 1px dotted #ccc; text-align: right; white-space: nowrap;">
          R$ ${(item.unit_price * item.quantity).toFixed(2)}
        </td>
      </tr>
    `).join('');

    return `
      <div style="font-family: 'Courier New', Courier, monospace; font-size: 12px; width: 280px; padding: 10px; color: #000; background: #fff;">
        <div style="text-align: center; font-weight: bold; font-size: 14px; margin-bottom: 5px;">
          ${storeName}
        </div>
        ${store?.whatsapp ? `<div style="text-align: center; font-size: 11px; margin-bottom: 10px;">Tel: ${store.whatsapp}</div>` : ''}
        
        <div style="border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 8px 0; margin: 8px 0;">
          <div style="font-weight: bold; font-size: 14px;">PEDIDO #${order.order_number}</div>
          <div style="font-size: 11px; color: #666;">${new Date(order.created_at).toLocaleString('pt-BR')}</div>
        </div>

        <div style="margin-bottom: 10px;">
          <div><strong>Cliente:</strong> ${order.customer_name}</div>
          <div><strong>Tel:</strong> ${order.customer_phone}</div>
          <div><strong>Tipo:</strong> <span style="font-weight: bold;">${isDelivery ? 'ENTREGA' : 'RETIRADA'}</span></div>
        </div>

        ${isDelivery && order.address ? `
          <div style="background: #f5f5f5; padding: 8px; margin-bottom: 10px; border-radius: 4px;">
            <div style="font-weight: bold; margin-bottom: 4px;">Endereco:</div>
            <div>${order.address.street || ''}, ${order.address.number || ''}</div>
            ${order.address.complement ? `<div>${order.address.complement}</div>` : ''}
            <div>${order.address.neighborhood || ''} - ${order.address.city || ''}</div>
            ${order.address.reference ? `<div style="font-size: 11px; color: #666; margin-top: 4px;">Ref: ${order.address.reference}</div>` : ''}
          </div>
        ` : ''}

        <div style="border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 8px 0; margin: 8px 0;">
          <div style="font-weight: bold; margin-bottom: 8px;">ITENS:</div>
          <table style="width: 100%; border-collapse: collapse;">
            ${items}
          </table>
        </div>

        <div style="margin-bottom: 10px;">
          <div style="display: flex; justify-content: space-between;">
            <span>Subtotal:</span>
            <span>R$ ${(order.subtotal || order.total).toFixed(2)}</span>
          </div>
          ${order.delivery_fee > 0 ? `
            <div style="display: flex; justify-content: space-between;">
              <span>Taxa entrega:</span>
              <span>R$ ${order.delivery_fee.toFixed(2)}</span>
            </div>
          ` : ''}
          ${order.discount > 0 ? `
            <div style="display: flex; justify-content: space-between; color: #16a34a;">
              <span>Desconto:</span>
              <span>-R$ ${order.discount.toFixed(2)}</span>
            </div>
          ` : ''}
        </div>

        <div style="border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 8px 0; margin: 8px 0;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 16px;">
            <span>TOTAL:</span>
            <span>R$ ${order.total.toFixed(2)}</span>
          </div>
        </div>

        <div style="margin-bottom: 10px;">
          <div style="display: flex; justify-content: space-between;">
            <span>Pagamento:</span>
            <span style="font-weight: bold;">${order.payment_method === 'pix' ? 'PIX' : order.payment_method === 'cash' ? 'DINHEIRO' : 'CARTAO'}</span>
          </div>
          ${order.payment_method === 'cash' && order.change_for ? `
            <div style="display: flex; justify-content: space-between;">
              <span>Troco para:</span>
              <span>R$ ${order.change_for}</span>
            </div>
          ` : ''}
        </div>

        ${order.notes ? `
          <div style="background: #fef3c7; padding: 8px; margin-bottom: 10px; border-radius: 4px; border-left: 3px solid #f59e0b;">
            <strong>Obs:</strong> ${order.notes}
          </div>
        ` : ''}

        <div style="text-align: center; margin-top: 15px; padding-top: 10px; border-top: 1px dashed #000;">
          <div style="font-size: 11px;">Obrigado pela preferencia!</div>
          <div style="font-weight: bold;">ADEGGA DELIVERY</div>
        </div>
      </div>
    `;
  }, [store]);

  // Print order using print-js
  const printOrder = useCallback((order) => {
    return new Promise((resolve, reject) => {
      try {
        setPrintingOrderId(order.id);
        
        // Create a temporary container
        const containerId = `print-station-${order.id}-${Date.now()}`;
        const container = document.createElement('div');
        container.id = containerId;
        container.innerHTML = generatePrintHTML(order);
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '0';
        document.body.appendChild(container);

        printJS({
          printable: containerId,
          type: 'html',
          targetStyles: ['*'],
          style: `
            @page { 
              size: 80mm auto; 
              margin: 0; 
            }
            @media print {
              body { 
                width: 80mm !important;
                margin: 0 !important;
                padding: 0 !important;
              }
            }
          `,
          onLoadingEnd: () => {
            // Cleanup
            setTimeout(() => {
              if (document.getElementById(containerId)) {
                document.body.removeChild(container);
              }
            }, 1000);
            
            // Mark as printed
            setPrintedOrders(prev => [order.id, ...prev.slice(0, 19)]);
            setPendingOrders(prev => prev.filter(o => o.id !== order.id));
            setPrintingOrderId(null);
            resolve({ success: true });
          },
          onError: (error) => {
            // Cleanup
            if (document.getElementById(containerId)) {
              document.body.removeChild(container);
            }
            setPrintingOrderId(null);
            reject(error);
          }
        });
      } catch (error) {
        setPrintingOrderId(null);
        reject(error);
      }
    });
  }, [generatePrintHTML]);

  // Handle new order
  const handleNewOrder = useCallback(async (orderData) => {
    // Play sound
    if (soundEnabled && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (e) {}
    }

    // Add to pending
    setPendingOrders(prev => [orderData, ...prev]);

    // Auto print if enabled
    if (autoPrint) {
      toast.info(`Imprimindo pedido #${orderData.order_number}...`);
      try {
        await printOrder(orderData);
        toast.success(`Pedido #${orderData.order_number} impresso!`);
      } catch (e) {
        toast.error(`Erro ao imprimir pedido #${orderData.order_number}`);
      }
    } else {
      toast.info(`Novo pedido #${orderData.order_number} - Clique para imprimir`);
    }
  }, [autoPrint, soundEnabled, printOrder]);

  // WebSocket connection
  useEffect(() => {
    if (!store?.id) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_URL}/ws/notifications/${store.id}`);
      
      ws.onopen = () => {
        console.log('PrintStation WebSocket connected');
        setConnected(true);
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_order' && data.order_data) {
            handleNewOrder(data.order_data);
          }
        } catch (e) {
          console.error('WebSocket message error:', e);
        }
      };

      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        // Reconnect after 3 seconds
        setTimeout(connect, 3000);
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [store?.id, handleNewOrder]);

  // Manual print
  const handleManualPrint = async (order) => {
    toast.info(`Imprimindo pedido #${order.order_number}...`);
    try {
      await printOrder(order);
      toast.success(`Pedido #${order.order_number} impresso!`);
    } catch (e) {
      toast.error('Erro ao imprimir');
    }
  };

  // Reprint order
  const handleReprint = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      await printOrder(res.data);
      toast.success('Pedido reimpresso!');
    } catch (e) {
      toast.error('Erro ao reimprimir');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Audio for notifications */}
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />

      {/* Header */}
      <header className="sticky top-0 bg-[#13131f] border-b border-white/10 px-4 py-3 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/app/pedidos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold flex items-center gap-2">
                <Printer className="w-5 h-5 text-[#ff6b00]" />
                Estacao de Impressao
              </h1>
              <p className="text-xs text-white/60">Deixe esta pagina aberta para imprimir pedidos automaticamente</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {connected ? (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Wifi className="w-3 h-3 mr-1" /> Conectado
              </Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                <WifiOff className="w-3 h-3 mr-1" /> Desconectado
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4">
        {/* Settings */}
        <Card className="bg-[#13131f] border-white/10 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={autoPrint}
                  onCheckedChange={setAutoPrint}
                  className="data-[state=checked]:bg-[#ff6b00]"
                />
                <span className="text-sm">Impressao automatica</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={soundEnabled}
                  onCheckedChange={setSoundEnabled}
                  className="data-[state=checked]:bg-[#ff6b00]"
                />
                <span className="text-sm flex items-center gap-1">
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  Som
                </span>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/app/configuracoes')}
              className="border-white/10"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configuracoes
            </Button>
          </div>
        </Card>

        {/* Print Status Info */}
        <Card className="bg-green-500/10 border-green-500/20 p-4 mb-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <p className="font-medium text-green-400">Sistema de impressao usando print-js</p>
              <p className="text-sm text-white/60">Metodo de impressao direto mais confiavel</p>
            </div>
          </div>
        </Card>

        {/* Pending Orders */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-400" />
            Pedidos Pendentes ({pendingOrders.length})
          </h2>
          
          {pendingOrders.length === 0 ? (
            <Card className="bg-[#13131f] border-white/10 p-8 text-center">
              <Printer className="w-12 h-12 mx-auto mb-3 text-white/20" />
              <p className="text-white/60">Nenhum pedido pendente</p>
              <p className="text-sm text-white/40">Novos pedidos aparecerao aqui automaticamente</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingOrders.map(order => (
                <Card key={order.id} className="bg-[#13131f] border-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-[#ff6b00]">Pedido #{order.order_number}</div>
                      <div className="text-sm text-white/60">{order.customer_name} - R$ {order.total?.toFixed(2)}</div>
                      <div className="text-xs text-white/40">
                        {order.items?.length} {order.items?.length === 1 ? 'item' : 'itens'} - 
                        {order.delivery_type === 'delivery' ? ' Entrega' : ' Retirada'}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleManualPrint(order)}
                      className="bg-[#ff6b00] hover:bg-[#ff8533]"
                      disabled={printingOrderId === order.id}
                    >
                      {printingOrderId === order.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                          Imprimindo...
                        </>
                      ) : (
                        <>
                          <Printer className="w-4 h-4 mr-2" />
                          Imprimir
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recently Printed */}
        <div>
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            Impressos Recentemente ({printedOrders.length})
          </h2>
          
          {printedOrders.length > 0 && (
            <div className="space-y-2">
              {printedOrders.slice(0, 10).map((orderId, i) => (
                <Card key={`${orderId}-${i}`} className="bg-[#13131f]/50 border-white/5 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/60">Pedido impresso</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReprint(orderId)}
                      className="text-white/60 hover:text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Reimprimir
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <Card className="bg-blue-500/10 border-blue-500/20 p-4 mt-6">
          <h3 className="font-semibold text-blue-400 mb-2">Como usar:</h3>
          <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
            <li>Deixe esta pagina <strong>aberta</strong> no computador conectado a impressora</li>
            <li>Quando um novo pedido chegar, ele sera <strong>impresso automaticamente</strong></li>
            <li>Se a impressao nao sair, clique em <strong>"Imprimir"</strong> manualmente</li>
            <li>Configure sua impressora como <strong>padrao</strong> no Windows</li>
          </ol>
          
          <div className="mt-3 pt-3 border-t border-blue-500/20">
            <p className="text-xs text-white/50">
              <strong>Dica:</strong> No Windows, va em Configuracoes - Dispositivos - Impressoras - 
              Clique na sua impressora - "Gerenciar" - "Definir como padrao"
            </p>
          </div>
        </Card>

        {/* Troubleshooting */}
        <Card className="bg-yellow-500/10 border-yellow-500/20 p-4 mt-4">
          <h3 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Problemas com impressao?
          </h3>
          <ul className="text-sm text-white/70 space-y-1 list-disc list-inside">
            <li>Verifique se a impressora esta <strong>ligada e conectada</strong></li>
            <li>Verifique se a impressora esta definida como <strong>padrao</strong></li>
            <li>Tente imprimir uma pagina de teste do Windows primeiro</li>
            <li>Reinicie o spooler de impressao: <code className="bg-black/30 px-1 rounded">services.msc</code> - Spooler de impressao - Reiniciar</li>
          </ul>
        </Card>
      </main>
    </div>
  );
};

export default PrintStation;
